import { FailedIcon, DoneIcon, WarningIcon } from "../assets/registtyIcons/icons";
import { useTranslation } from "react-i18next";

/**
 * ✅ UI matches the OLD component exactly:
 * - same wrapper structure
 * - same label span: {t(matchText)}:
 * - same non-existent branch (negative-value + t("notExistent"))
 * - same 2 separate spans: label then percent (t("matchPercentage") then {percent}%)
 *
 * ⚠️ Logic/functions are unchanged.
 */

const RenderMatchStatus = ({
  value1,
  value2,
  matchText = "matchingStatus", // ✅ old default
  spanStyle = "u-width--min-width_250",
}) => {
  const { t } = useTranslation("general");

  const { percent, level } = getMatchPercentage(value1, value2, {
    minWordSim: 0.85,
    minAbbrevSim: 0.55,
    abbrevSim: 0.65,
    stripLegal: true,
    dropShortWords: 2,
    ignoreCountries: true,
    minNonCountryTokensToIgnoreCountries: 3,
  });

  // ✅ OLD behavior: "notExistent" UI when one/both values missing
  // (in this new logic, treat empty input as "notExistent")
  if (
    value1 == null ||
    value2 == null ||
    value1.toString().trim() === "" ||
    value2.toString().trim() === ""
  ) {
    return (
      <div className="o-column-row-switcher">
        <span className={`${spanStyle}`}>{t(matchText)}:</span>
        <div className="o-row o-row--gap-size_100 o-row--vertical-align_center">
          <FailedIcon width={20} height={20} />
          <span className={`t-bold_bold t-body-text negative-value`}>
            {t("notExistent")}
          </span>
        </div>
      </div>
    );
  }

  // ✅ OLD config logic (exact thresholds)
  const getStatusConfig = (lvl) => {
    if (lvl === "high") {
      return {
        icon: <DoneIcon width={20} height={20} />,
        textClass: "confirm-text",
      };
    } else if (lvl === "medium" || lvl === "low") {
      // old code used WarningIcon for >=0, and percent is always >=0 here
      return {
        icon: <WarningIcon width={20} height={20} />,
        textClass: "warning-text",
      };
    } else {
      return {
        icon: <FailedIcon width={20} height={20} />,
        textClass: "error-text",
      };
    }
  };

  // But we want to match OLD percent-based icon rules exactly:
  const getStatusConfigByPercent = (p) => {
    if (p >= 90) {
      return {
        icon: <DoneIcon width={20} height={20} />,
        textClass: "confirm-text",
        textStyle: undefined,
      };
    }

    // ✅ 0%: same sizing as the rest (warning-text), but force red via inline style
    if (p === 0) {
      return {
        icon: <FailedIcon width={20} height={20} />,
        textClass: "warning-text",
        textStyle: { color: "#D32F2F" },
      };
    }

    if (p > 0) {
      return {
        icon: <WarningIcon width={20} height={20} />,
        textClass: "warning-text",
        textStyle: undefined,
      };
    }

    return {
      icon: <FailedIcon width={20} height={20} />,
      textClass: "warning-text",
      textStyle: undefined,
    };
  };

  const config = getStatusConfigByPercent(percent);

  return (
    <div className="o-column-row-switcher">
      <span className={`${spanStyle}`}>{t(matchText)}:</span>
      <div className="o-row o-row--gap-size_100 o-row--vertical-align_center">
        {config.icon}
        <span
          className={`t-body-text t-bold_bold ${config.textClass}`}
          style={config.textStyle}
        >
          {t("matchPercentage")}
        </span>

        <span
          className={`t-body-text t-bold_bold ${config.textClass}`}
          style={config.textStyle}
        >
          {percent}%
        </span>
      </div>
    </div>
  );
};

function getMatchPercentage(value1, value2, options = {}) {
  const a = value1 == null ? "" : value1.toString();
  const b = value2 == null ? "" : value2.toString();

  const tokensA = tokenizeText(a, options);
  const tokensB = tokenizeText(b, options);

  if (tokensA.length === 0 || tokensB.length === 0) {
    return { percent: 0, level: "low" };
  }

  const scoreAB = directionalTokenScore(tokensA, tokensB, options);
  const scoreBA = directionalTokenScore(tokensB, tokensA, options);

  const percent = Math.round(((scoreAB + scoreBA) / 2) * 100);
  const level = percent >= 90 ? "high" : percent >= 60 ? "medium" : "low";

  return { percent, level };
}

function tokenizeText(
  input,
  {
    stripLegal = true,
    dropShortWords = 2,
    ignoreCountries = true,
    minNonCountryTokensToIgnoreCountries = 3,
  } = {},
) {
  const LEGAL = new Set([
    "LTD",
    "LIMITED",
    "CO",
    "COMPANY",
    "INC",
    "LLC",
    "CORP",
    "CORPORATION",
    "GMBH",
    "BV",
    "SA",
    "AS",
    "AŞ",
    "A.Ş",
    "SAN",
    "TIC",
    "TİC",
    "VE",
    "V",
  ]);

  const COUNTRIES = new Set([
    "TURKIYE",
    "TURKEY",
    "CUMHURIYETI",
    "REPUBLIC",
    "CHINA",
    "PRC",
    "CN",
    "CIN",
  ]);

  const normalized = (input || "")
    .toUpperCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

  let tokens = normalized.split(" ").filter(Boolean);

  tokens = tokens.filter((t) => {
    const isNumber = /^\d+$/.test(t);
    const isImportantShort = t === "NO";
    if (isNumber || isImportantShort) return true;
    return t.length > dropShortWords;
  });

  if (stripLegal) {
    tokens = tokens.filter((t) => !LEGAL.has(t));
  }

  if (ignoreCountries) {
    const countryTokens = tokens.filter((t) => COUNTRIES.has(t));
    const nonCountryTokens = tokens.filter((t) => !COUNTRIES.has(t));

    if (nonCountryTokens.length >= minNonCountryTokensToIgnoreCountries) {
      return nonCountryTokens;
    }

    return [...nonCountryTokens, ...countryTokens];
  }

  return tokens;
}

function directionalTokenScore(
  tokensA,
  tokensB,
  { minWordSim = 0.85, minAbbrevSim = 0.55, abbrevSim = 0.65 } = {},
) {
  const used = new Set();
  let weightedSum = 0;
  let weightTotal = 0;

  for (const wA of tokensA) {
    const weight = tokenWeight(wA);
    weightTotal += weight;

    let bestScore = 0;
    let bestIdx = -1;

    for (let i = 0; i < tokensB.length; i++) {
      if (used.has(i)) continue;

      const wB = tokensB[i];
      const sim = wordSimilarity(wA, wB);

      const isAbbrev = isAbbrevPair(wA, wB);
      const candidateScore = isAbbrev
        ? sim >= minAbbrevSim
          ? abbrevSim
          : 0
        : sim >= minWordSim
          ? sim
          : 0;

      if (candidateScore > bestScore) {
        bestScore = candidateScore;
        bestIdx = i;
      }
    }

    if (bestIdx !== -1 && bestScore > 0) {
      used.add(bestIdx);
      weightedSum += bestScore * weight;
    }
  }

  return weightTotal === 0 ? 0 : weightedSum / weightTotal;
}

function tokenWeight(token) {
  if (/^\d+$/.test(token)) return 5;
  const len = token.length;
  return Math.max(3, Math.min(len, 10));
}

function isAbbrevPair(a, b) {
  const map = {
    MAH: "MAHALLESI",
    MH: "MAHALLESI",
    BULV: "BULVARI",
    BLV: "BULVARI",
    CAD: "CADDESI",
    CD: "CADDESI",
    SK: "SOKAK",
    SOK: "SOKAK",
  };

  if (map[a] && map[a] === b) return true;
  if (map[b] && map[b] === a) return true;
  return false;
}

function wordSimilarity(a, b) {
  if (a === b) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshtein(a, b);
  return 1 - dist / maxLen;
}

function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  return dp[m][n];
}

export default RenderMatchStatus;
